import {
  Link,
  Text,
  Wrap,
  Box,
} from '@chakra-ui/react';
import {
  useTotalTrafficLaneKm,
  useVKT,
} from '@src/modules/performance/context/reports-api-hooks';
import Chart from 'react-apexcharts';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import StackedBarChart from '@src/modules/performance/components/charts/StackedBarChart';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import { useRecoilValue } from 'recoil';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Total Road Use',
  content: [
    {
      title: 'Vehicle kilometres travelled (VKT)',
      description:
  <Text fontSize="inherit" mb={2}>
    Estimated total kilometres travelled by vehicles on the selected road network per year.
  </Text>,
      source: null,
    },
    {
      title: 'Total traffic per lane km',
      description:
  <Text fontSize="inherit" mb={2}>
    Estimated kilometres travelled by all road vehicles divided by the total lane kilometer length, for the selected road network per year.
    For RCAs, the chart also shows the average estimated vkt per lane km of the RCA’s peer group.
    The chart also shows the average estimated vkt per lane km for the National road network.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools" isExternal>NZTA, vehicle use</Link>
  </Text>,
    },
  ],
};

function RoadNetworkUseRoadUse() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: vktData, isLoading: isLoadingVkt } = useVKT();
  const { data: totalTrafficLaneKmData, isLoading: isLoadingTotalTrafficLaneKm } = useTotalTrafficLaneKm();
  const isLoading = isLoadingVkt || isLoadingTotalTrafficLaneKm;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Check if all lockYears are the same
  const allLockYears = [
    vktData?.[0]?.LockYear,
    totalTrafficLaneKmData?.[0]?.lockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // VKT chart data
  const vktYears = [...new Set(vktData?.map((item) => item.Year))];
  const vktChartData = [
    {
      name: 'Urban',
      data: vktData
        ?.filter((x) => x.RoadType === 'U' || x.RoadType === 'Urban')
        .map((item) => ({
          x: item.Year,
          y: item.VKT / 1000000,
        })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Rural',
      data: vktData
        ?.filter((x) => x.RoadType === 'R' || x.RoadType === 'Rural')
        .map((item) => ({
          x: item.Year,
          y: item.VKT / 1000000,
        })),
      color: CHART_COLOURS.red,
    },
  ];

  // Total Traffic Lane Km chart data
  const totalTrafficYears = [...new Set(totalTrafficLaneKmData?.map((item) => item.year))];
  const totalTrafficData = [
    {
      name: currentDatabase?.name,
      data: totalTrafficLaneKmData?.filter((x) => x.networkOrder === 1).map((item) => ({
        x: item.year,
        y: Math.round(item.vktPerLaneKm),
      })),
      color: CHART_COLOURS.blue,
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          data: totalTrafficLaneKmData?.filter((x) => x.networkOrder === 3).map((item) => ({
            x: item.year,
            y: Math.round(item.vktPerLaneKm),
          })),
          color: CHART_COLOURS.lightgrey,
        },
      ]
      : []),
    {
      name: 'Peer Group',
      data: totalTrafficLaneKmData?.filter((x) => x.networkOrder === 2).map((item) => ({
        x: item.year,
        y: Math.round(item.vktPerLaneKm),
      })),
      color: CHART_COLOURS.black,
    },
  ];

  // Filter out the Peer Group and National data if it's a national report, and Peer Group if it's a region or state highways report
  const totalTrafficChartData = filterChartDataByName(
    totalTrafficData,
    chartFilters,
  );

  return (
    <ReportSection
      title="Total Road Use"
      isLoading={isLoading || !vktData || !totalTrafficLaneKmData}
      informationSource={informationSource}
      lockYear={primaryLockYear || null}
    >
      <Wrap spacing={0}>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={vktData?.[0]?.LockYear}
          />
          )}
          <StackedBarChart
            title="Vehicle kilometres travelled (VKT)"
            categories={vktYears}
            series={vktChartData}
            width="100%"
            height="280"
            prefix=""
            suffix="M"
            decimalPoints={0}
            yAxisDecimalPoints={0}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={totalTrafficLaneKmData?.[0]?.lockYear}
          />
          )}
          <Chart
            options={{
              title: {
                text: 'Total Traffic per Lane km',
              },
              chart: {
                animations: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: totalTrafficYears,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
              },
              yaxis: [
                {
                  title: {
                    text: 'VKT per lane km',
                  },
                  forceNiceScale: false,
                  labels: {
                    formatter(y) {
                      return `${y.toFixed(0)}`;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 10,
                },
              },
              dataLabels: {
                enabled: false,
              },
              tooltip: {
                shared: true,
                y: {
                  formatter: (val) => `${val.toFixed(0)}`,
                },
              },
            }}
            series={totalTrafficChartData}
            type="line"
            width="100%"
            height="280"
          />
        </Box>
      </Wrap>
      <GroupedRegionMessage rcaIds={[1014, 1015, 1016]} />
    </ReportSection>
  );
}

export default RoadNetworkUseRoadUse;
